import TrailerCarouselVideo from '@/app/[locale]/[roomId]/components/TrailerCarouselVideo'
import ArrowLeftIcon from '@/components/assets/icon_arrow_left.svg'
import { MEDIA_TYPE } from '@/hooks/useTrailerViewMedia'
import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useEventListener } from 'usehooks-ts'

export interface Media {
  type: (typeof MEDIA_TYPE)[keyof typeof MEDIA_TYPE]
  url: string
  duration?: number
  playbackToken?: string
}

interface TrailerCarouselSlideProps {
  media: Media
  onEnded: () => void
  mediaList: Media[]
  currentIndex: number
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>
  playKey: number
}

const TrailerCarouselSlide = ({
  media,
  onEnded,
  mediaList,
  currentIndex,
  setCurrentIndex,
  playKey,
}: TrailerCarouselSlideProps) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const { type, url, playbackToken } = media || {}

  const handleClickArrow = (move: number) => {
    setCurrentIndex((prev) => {
      const nextIndex = prev + move
      if (nextIndex < 0) {
        return mediaList.length - 1
      } else if (nextIndex >= mediaList.length) {
        return 0
      } else {
        return nextIndex
      }
    })
  }

  useEffect(() => {
    const hasItems = mediaList?.length > 1
    const isImage = type === MEDIA_TYPE.IMAGE

    setCanScrollLeft(hasItems && isImage)
    setCanScrollRight(hasItems && isImage)
  }, [currentIndex, mediaList, type])

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== 'https://showroom.one.blendvision.com') return

      let message = event.data
      if (typeof message === 'string') {
        try {
          message = JSON.parse(message)
        } catch {
          return
        }
      }
      const playlogEvent = message?.info?.playlogEventName
      if (playlogEvent === 'playback_video_ended') {
        onEnded()
      }
    },
    [onEnded],
  )
  useEventListener('message', handleMessage)

  if (!url) return null

  return (
    <>
      {type === MEDIA_TYPE.VIDEO ? (
        <div key={playKey} className="h-full w-full">
          <TrailerCarouselVideo
            url={url}
            playbackToken={playbackToken}
            iframeRef={iframeRef}
            onEnded={onEnded}
          />
        </div>
      ) : (
        <>
          <Image
            src={url}
            alt="trailer-carousel-slide"
            fill
            className="z-0 h-full w-full object-cover"
          />
          {canScrollLeft && (
            <ArrowLeftIcon
              className="absolute top-1/2 left-0 m-5 h-6 w-6 -translate-y-1/2 cursor-pointer"
              onClick={() => handleClickArrow(-1)}
            />
          )}
          {canScrollRight && (
            <ArrowLeftIcon
              className="absolute top-1/2 right-0 m-5 h-6 w-6 -translate-y-1/2 rotate-180 cursor-pointer"
              onClick={() => handleClickArrow(1)}
            />
          )}
        </>
      )}
    </>
  )
}

export default TrailerCarouselSlide
